// src/features/multiplayer/quiz/quizHelpers.js
// Quiz constants, scoring, answer-key storage/backup and question generation.
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../shared/firebase/services';
import { designTokens } from '../../../shared/theme/theme';
import { getFallbackQuestions } from './quizFallbackBank';

export const QUIZ_TOPICS = [
  { id: 'general',    label: 'General Knowledge', icon: '🌍', color: designTokens.games.quizTopics.general },
  { id: 'science',    label: 'Science',            icon: '🔬', color: designTokens.games.quizTopics.science },
  { id: 'history',    label: 'History',            icon: '🏛️',  color: designTokens.games.quizTopics.history },
  { id: 'geography',  label: 'Geography',          icon: '🗺️',  color: designTokens.games.quizTopics.geography },
  { id: 'sports',     label: 'Sports',             icon: '⚽',  color: designTokens.games.quizTopics.sports },
  { id: 'movies',     label: 'Movies & TV',        icon: '🎬',  color: designTokens.games.quizTopics.movies },
  { id: 'music',      label: 'Music',              icon: '🎵',  color: designTokens.games.quizTopics.music },
  { id: 'technology', label: 'Technology',         icon: '💻',  color: designTokens.games.quizTopics.technology },
  { id: 'food',       label: 'Food & Drink',        icon: '🍕',  color: designTokens.games.quizTopics.food },
  { id: 'nature',     label: 'Nature',              icon: '🌿',  color: designTokens.games.quizTopics.nature },
];

export const TOPIC_MAP = Object.fromEntries(QUIZ_TOPICS.map(t => [t.id, t]));

export const QUIZ_SETTINGS = {
  minPlayers: 2,
  maxPlayers: 12,
  questionCount: 8,
  answerTime: 20,
  revealTime: 4,
  maxScore: 1000,
};

export const POINTS_PER_SECOND = Math.floor(QUIZ_SETTINGS.maxScore / QUIZ_SETTINGS.answerTime);
export const OPTION_LABELS = ['A', 'B', 'C', 'D'];
export const OPTION_COLORS = designTokens.games.quizOptions;

export function toPublicQuizQuestions(questions) {
  return questions.map(({ question, options, resource }) => ({
    question,
    options,
    resource: resource || '',
  }));
}

export function getQuizAnswerResult(optionIndex, correctIndex, submittedAt, startedAt) {
  if (!Number.isInteger(optionIndex) || optionIndex < 0 || optionIndex > 3 ||
      !Number.isInteger(correctIndex) || correctIndex < 0 || correctIndex > 3 ||
      !Number.isFinite(submittedAt) || !Number.isFinite(startedAt)) {
    return { correct: false, score: 0, late: true };
  }

  const elapsed = Math.max(0, (submittedAt - startedAt) / 1000);
  if (elapsed > QUIZ_SETTINGS.answerTime) return { correct: false, score: 0, late: true };

  const correct = optionIndex === correctIndex;
  const score = correct
    ? Math.min(QUIZ_SETTINGS.maxScore, Math.max(
      50,
      QUIZ_SETTINGS.maxScore - Math.floor(elapsed * POINTS_PER_SECOND)
    ))
    : 0;
  return { correct, score, late: false };
}

const storageKey = roomId => `celebrations.quizAnswerKey.${roomId}`;

const isValidAnswers = answers => Array.isArray(answers) && answers.length > 0 &&
  answers.every(index => Number.isInteger(index) && index >= 0 && index <= 3);

export function createQuizAnswerKeyRecord(questions) {
  const answers = questions.map(question => question.correctIndex);
  if (!isValidAnswers(answers)) throw new Error('Cannot store an invalid quiz answer key.');
  return { answers, fingerprint: JSON.stringify(toPublicQuizQuestions(questions)) };
}

export function cacheQuizAnswerKey(roomId, record) {
  window.sessionStorage.setItem(storageKey(roomId), JSON.stringify(record));
}

export function storeQuizAnswerKey(roomId, questions) {
  if (!roomId) throw new Error('Cannot store an invalid quiz answer key.');
  cacheQuizAnswerKey(roomId, createQuizAnswerKeyRecord(questions));
}

export function readQuizAnswerKeyRecord(record, questions) {
  if (isValidAnswers(record?.answers) &&
      (!questions || record.fingerprint === JSON.stringify(toPublicQuizQuestions(questions)))) {
    return record.answers;
  }
  return null;
}

export function loadQuizAnswerKey(roomId, questions) {
  if (!roomId) return null;
  const stored = window.sessionStorage.getItem(storageKey(roomId));
  if (!stored) return null;
  try {
    return readQuizAnswerKeyRecord(JSON.parse(stored), questions);
  } catch {
    return null;
  }
}

const keyRef = roomId => doc(db, 'rooms', roomId, 'private', 'quizKey');

/** Keeps the answer key in this tab and in a host-only document so a reopened host tab can recover it. */
export async function persistQuizAnswerKey(roomId, questions) {
  const record = createQuizAnswerKeyRecord(questions);
  cacheQuizAnswerKey(roomId, record);
  try {
    await setDoc(keyRef(roomId), record);
  } catch (error) {
    console.warn('Could not back up the quiz answer key:', error);
  }
}

/** Reads the answer key from this tab, falling back to the host-only backup. */
export async function recoverQuizAnswerKey(roomId, questions) {
  const local = loadQuizAnswerKey(roomId, questions);
  if (local) return local;

  try {
    const snapshot = await getDoc(keyRef(roomId));
    const record = snapshot.exists() ? snapshot.data() : null;
    const answers = readQuizAnswerKeyRecord(record, questions);
    if (answers) cacheQuizAnswerKey(roomId, record);
    return answers;
  } catch (error) {
    console.warn('Could not recover the quiz answer key:', error);
    return null;
  }
}

// Generates quiz questions through the host-only `generateQuizQuestions` Cloud Function, which keeps the
// Groq API key on the server. Falls back to the built-in question bank if the function is unavailable.
const generateQuestionsCallable = httpsCallable(functions, 'generateQuizQuestions', { timeout: 60_000 });

function withSource(questions, source) {
  Object.defineProperty(questions, '_source', { value: source, enumerable: false });
  return questions;
}

export async function generateQuizQuestions(topic, count = 8, roomId) {
  if (!roomId) return withSource(getFallbackQuestions(topic, count), 'fallback');

  try {
    const { data } = await generateQuestionsCallable({ roomId, topic, count });
    const questions = (data?.questions || []).slice(0, count).map(question => ({
      question: question.question,
      options: question.options.map(String),
      correctIndex: question.correctIndex,
      resource: '',
    }));
    if (questions.length === 0) throw new Error('No questions returned');
    if (questions.length >= count) return withSource(questions, 'ai');

    const extra = getFallbackQuestions(topic, count - questions.length);
    return withSource([...questions, ...extra], 'ai');
  } catch (error) {
    console.warn('AI question generation unavailable — using the built-in bank:', error.message);
    return withSource(getFallbackQuestions(topic, count), 'fallback');
  }
}
